/**
 * マスター辞書定義の型
 * 各キーに対して ja/en のデフォルト値と編集可否フラグを持つ
 */

/** 1つの用語の定義 */
export interface TermDef {
    /** 日本語デフォルト値 */
    ja: string;
    /** 英語デフォルト値 */
    en: string;
    /** true の場合、表示設定画面で上書き可能 */
    edit_flg: boolean;
}

/** マスター辞書（キー → 定義） */
export type MasterDictionary = Record<string, TermDef>;

/**
 * マスター辞書から i18next 用のフラットなリソースを抽出する
 * @param master マスター辞書オブジェクト
 * @param lang 言語コード ("ja" | "en")
 * @returns { KEY: "翻訳値", ... }
 */
export function extractResources(
    master: MasterDictionary,
    lang: "ja" | "en",
): Record<string, string> {
    const resources: Record<string, string> = {};
    for (const [key, def] of Object.entries(master)) {
        resources[key] = def[lang];
    }
    return resources;
}

/**
 * マスター辞書から編集可能なキー一覧を抽出する
 * @param master マスター辞書オブジェクト
 * @returns 編集可能なキーの配列
 */
export function extractEditableKeys(master: MasterDictionary): string[] {
    return Object.entries(master)
        .filter(([, def]) => def.edit_flg)
        .map(([key]) => key);
}

/** DisplaySettings 用の編集可能項目 */
export interface EditableTerm {
    /** i18next 用のキー（namespace.key 形式） */
    fullKey: string;
    /** namespace */
    ns: string;
    /** キー名 */
    key: string;
    /** デフォルト値（現在の言語） */
    defaultValue: string;
    /** 現在の上書き値（Redis）。未設定なら undefined */
    override?: string;
}

/**
 * 複数のマスター辞書から edit_flg: true の項目を EditableTerm[] として抽出する
 * @param masters { namespace: MasterDictionary } のマップ
 * @param lang 言語コード
 * @param overrides Redis から取得した上書き値 { "namespace.key": "value" }
 */
export function extractEditableTerms(
    masters: Record<string, MasterDictionary>,
    lang: "ja" | "en",
    overrides: Record<string, string>,
): EditableTerm[] {
    const terms: EditableTerm[] = [];
    for (const [ns, master] of Object.entries(masters)) {
        for (const [key, def] of Object.entries(master)) {
            if (!def.edit_flg) continue;
            const fullKey = `${ns}.${key}`;
            terms.push({
                fullKey,
                ns,
                key,
                defaultValue: def[lang],
                override: overrides[fullKey],
            });
        }
    }
    return terms;
}
